import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FileText, Calendar, Mail, DollarSign, ChevronDown, ChevronUp, Phone, StickyNote } from 'lucide-react';
import { fetchAdminQuotes } from '../../services/adminApi';

const PageContainer = styled.div`
  width: 100%;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, var(--primary-light) 0%, rgba(255, 143, 0, 0.05) 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 143, 0, 0.2);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: var(--gray-100);
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid var(--gray-200);
  cursor: pointer;

  &:hover {
    background-color: var(--gray-50);
  }
`;

const Td = styled.td`
  padding: 1rem;
  font-size: 0.9375rem;
  color: var(--gray-700);
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--gray-500);
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.$status) {
      case 'pending': return 'var(--warning-light)';
      case 'reviewed': return '#E3F2FD';
      case 'converted': return 'var(--success-light)';
      case 'declined': return 'var(--danger-light)';
      default: return 'var(--gray-200)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending': return 'var(--warning-color)';
      case 'reviewed': return '#1976D2';
      case 'converted': return 'var(--success-color)';
      case 'declined': return 'var(--danger-color)';
      default: return 'var(--gray-600)';
    }
  }};
`;

const ExpandableRow = styled.tr`
  background-color: var(--gray-50);
`;

const ExpandableCell = styled.td`
  padding: 1.5rem;
`;

const DetailSection = styled.div`
  margin-bottom: 1rem;
`;

const DetailLabel = styled.strong`
  color: var(--secondary-color);
  display: inline-block;
  min-width: 120px;
`;

const ItemsList = styled.div`
  margin-top: 1rem;
`;

const ItemCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid var(--gray-200);
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
`;

const ItemDetail = styled.div`
  font-size: 0.875rem;
  color: var(--gray-700);
`;

const ItemLabel = styled.div`
  font-size: 0.75rem;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.125rem;
  color: var(--gray-500);
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.125rem;
  color: var(--danger-color);
`;

const QuoteRequestsPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAdminQuotes();
      setQuotes(data || []);
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  if (loading) {
    return <LoadingText>Loading quote requests...</LoadingText>;
  }

  if (error) {
    return <ErrorText>Error loading quotes: {error}</ErrorText>;
  }

  const totalAmount = quotes.reduce((sum, quote) => sum + (parseFloat(quote.total_amount) || 0), 0);
  const avgAmount = quotes.length > 0 ? totalAmount / quotes.length : 0;

  return (
    <PageContainer>
      <StatsContainer>
        <StatCard>
          <StatValue>{quotes.length}</StatValue>
          <StatLabel>Total Quotes</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(totalAmount)}</StatValue>
          <StatLabel>Total Value</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(avgAmount)}</StatValue>
          <StatLabel>Average Value</StatLabel>
        </StatCard>
      </StatsContainer>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Reference</Th>
              <Th>Email</Th>
              <Th>Total Amount</Th>
              <Th>Status</Th>
              <Th>Created At</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {quotes.map((quote) => (
              <React.Fragment key={quote.id}>
                <Tr onClick={() => toggleRow(quote.id)}>
                  <Td>
                    <IconWrapper>
                      <FileText size={16} />
                      {quote.reference}
                    </IconWrapper>
                  </Td>
                  <Td>
                    <IconWrapper>
                      <Mail size={16} />
                      {quote.client_email}
                    </IconWrapper>
                  </Td>
                  <Td>
                    <IconWrapper>
                      <DollarSign size={16} />
                      {formatCurrency(quote.total_amount)}
                    </IconWrapper>
                  </Td>
                  <Td>
                    <StatusBadge $status={quote.status}>
                      {quote.status}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <IconWrapper>
                      <Calendar size={16} />
                      {formatDate(quote.created_at)}
                    </IconWrapper>
                  </Td>
                  <Td>
                    {expandedRow === quote.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </Td>
                </Tr>
                {expandedRow === quote.id && (
                  <ExpandableRow>
                    <ExpandableCell colSpan={6}>
                      <DetailSection>
                        <DetailLabel>Phone:</DetailLabel> {quote.phone_number || 'N/A'}
                      </DetailSection>
                      {quote.additional_notes && (
                        <DetailSection>
                          <IconWrapper>
                            <StickyNote size={16} />
                            <DetailLabel>Notes:</DetailLabel>
                          </IconWrapper>
                          <div style={{ marginLeft: '36px', marginTop: '0.5rem', color: 'var(--gray-600)' }}>
                            {quote.additional_notes}
                          </div>
                        </DetailSection>
                      )}
                      <DetailSection>
                        <DetailLabel>Discount:</DetailLabel>
                        {quote.discount_enabled ? (
                          <>
                            {quote.discount_percentage}% ({formatCurrency(quote.discount_amount)})
                          </>
                        ) : (
                          'None'
                        )}
                      </DetailSection>
                      <DetailSection>
                        <DetailLabel>Subtotal:</DetailLabel> {formatCurrency(quote.subtotal)}
                      </DetailSection>
                      <ItemsList>
                        <DetailLabel>Items:</DetailLabel>
                        {quote.items && Array.isArray(quote.items) && quote.items.length > 0 ? (
                          quote.items.map((item, idx) => (
                            <ItemCard key={idx}>
                              <ItemDetail>
                                <ItemLabel>Product</ItemLabel>
                                <strong>{item.name || item.product?.name || 'Unknown'}</strong>
                              </ItemDetail>
                              <ItemDetail>
                                <ItemLabel>Quantity</ItemLabel>
                                {item.quantity}
                              </ItemDetail>
                              <ItemDetail>
                                <ItemLabel>Unit Price</ItemLabel>
                                {formatCurrency(item.unitPrice || item.price || 0)}
                              </ItemDetail>
                              <ItemDetail>
                                <ItemLabel>Total</ItemLabel>
                                <strong>{formatCurrency(item.totalPrice || 0)}</strong>
                              </ItemDetail>
                            </ItemCard>
                          ))
                        ) : (
                          <div style={{ marginLeft: '36px', color: 'var(--gray-500)' }}>No items</div>
                        )}
                      </ItemsList>
                    </ExpandableCell>
                  </ExpandableRow>
                )}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default QuoteRequestsPage;
